
import React, { useRef, useEffect, useState, useMemo, Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useLocation,
  ScrollRestoration,
  useRouteError,
  isRouteErrorResponse,
  Link,
  useSearchParams,
  useNavigate,
  Navigate
} from 'react-router-dom';
import { Home } from './pages/Home'; // Critical path - keep eager
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { BookingModal } from './components/BookingModal';
import { ContactModal } from './components/ContactModal';
import { CookieConsent } from './components/CookieConsent';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { BackButton } from './components/BackButton';
import { InfoButton } from './components/InfoButton';
import { ContentProvider, useContent } from './contexts/ContentContext';
import { AlertTriangle, RefreshCw, Home as HomeIcon, Loader2 } from 'lucide-react';
import { ContextType } from './types';

// Lazy Load Pages
const AboutPage = lazy(() => import('./pages/AboutPage').then(module => ({ default: module.AboutPage })));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail').then(module => ({ default: module.ProjectDetail })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(module => ({ default: module.ContactPage })));
const TechnologyDetail = lazy(() => import('./pages/TechnologyDetail').then(module => ({ default: module.TechnologyDetail })));
const TechnologiesPage = lazy(() => import('./pages/TechnologiesPage').then(module => ({ default: module.TechnologiesPage })));
const ServicesPage = lazy(() => import('./pages/ServicesPage').then(module => ({ default: module.ServicesPage })));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail').then(module => ({ default: module.ServiceDetail })));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage').then(module => ({ default: module.ProjectsPage })));
const TeamPage = lazy(() => import('./pages/TeamPage').then(module => ({ default: module.TeamPage })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/TermsOfService').then(module => ({ default: module.TermsOfService })));
const Imprint = lazy(() => import('./pages/Imprint').then(module => ({ default: module.Imprint })));
const CookiesPage = lazy(() => import('./pages/CookiesPage').then(module => ({ default: module.CookiesPage })));
const CareersPage = lazy(() => import('./pages/CareersPage').then(module => ({ default: module.CareersPage })));
const FAQPage = lazy(() => import('./pages/FAQPage').then(module => ({ default: module.FAQPage })));
const SitemapPage = lazy(() => import('./pages/SitemapPage').then(module => ({ default: module.SitemapPage })));
const Admin = lazy(() => import('./pages/Admin').then(module => ({ default: module.Admin })));
const GamePage = lazy(() => import('./pages/GamePage').then(module => ({ default: module.GamePage })));

const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center min-h-[50vh] w-full">
    <Loader2 className="w-8 h-8 md:w-12 md:h-12 text-accent animate-spin" />
  </div>
);

const ErrorPage: React.FC = () => {
  // ... (keep existing implementation)
  const error = useRouteError();
  let errorTitle = "Navigation Error";
  if (isRouteErrorResponse(error)) {
    errorTitle = `${error.status} ${error.statusText}`;
  }

  return (
    <div className="min-h-screen bg-[#0d0a0f] flex items-center justify-center p-6 bg-grid">
      <div className="max-w-md w-full bg-[#1a141f] border border-white/10 p-10 rounded-[40px] text-center shadow-2xl">
        <div className="w-20 h-20 bg-red-500/10 border border-red-500/20 rounded-3xl flex items-center justify-center mx-auto mb-8 text-red-500">
          <AlertTriangle size={32} />
        </div>
        <h1 className="text-2xl font-gaming font-bold text-white mb-4 uppercase tracking-tighter leading-none">{errorTitle}</h1>
        <div className="flex gap-4">
          <button onClick={() => window.location.reload()} className="btn-secondary flex-1 py-3 flex items-center justify-center gap-2 text-xs uppercase font-bold"><RefreshCw size={16} /> Reload</button>
          <Link to="/" className="btn-primary flex-[2] py-3 flex items-center justify-center gap-2"><HomeIcon size={16} /> Home</Link>
        </div>
      </div>
    </div>
  );
};

const WebGLBackground: React.FC = () => {
  // ... (keep existing implementation)
  const { isTheatreMode } = useContent();
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isIframeReady, setIsIframeReady] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleLoad = () => {
      const isMobile = window.innerWidth < 768;
      const delay = isMobile ? 5000 : 2000;
      const timer = setTimeout(() => {
        setShouldLoad(true);
      }, delay);
      return () => clearTimeout(timer);
    };

    if (document.readyState === 'complete') {
      handleLoad();
    } else {
      window.addEventListener('load', handleLoad);
      return () => window.removeEventListener('load', handleLoad);
    }
  }, []);

  useEffect(() => {
    if (isTheatreMode && isIframeReady && iframeRef.current) {
      const focusTimer = setTimeout(() => {
        iframeRef.current?.focus();
      }, 100);
      return () => clearTimeout(focusTimer);
    }
  }, [isTheatreMode, isIframeReady]);

  return (
    <div
      className={`transition-all duration-1000 ease-in-out ${isTheatreMode
        ? 'fixed inset-0 z-[100] opacity-100'
        : isHome
          ? 'absolute top-0 left-0 w-full h-screen z-0 opacity-100'
          : 'fixed inset-0 z-[-1] opacity-0 pointer-events-none'
        }`}
    >
      <div className="absolute inset-0 bg-[#0d0a0f] hero-static-bg overflow-hidden">
        {/* Layer 0: Placeholder Loading Image */}
        <img
          src="/images/UI_Loading_Screen.webp"
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] z-0"
          alt=""
          loading="eager"
        />

        {/* Layer 10: The Game Iframe */}
        {shouldLoad && (
          <iframe
            ref={iframeRef}
            src="https://game.saltiumgames.com"
            loading="lazy"
            tabIndex={0}
            style={{
              visibility: isIframeReady ? 'visible' : 'hidden',
              pointerEvents: isIframeReady ? 'auto' : 'none'
            }}
            className={`w-full h-full border-none outline-none bg-transparent transition-opacity duration-[1500ms] z-10 relative ${isIframeReady ? 'opacity-100' : 'opacity-0'}`}
            title="Saltium Games Experience"
            allow="autoplay; fullscreen; xr-spatial-tracking"
            onLoad={() => setIsIframeReady(true)}
          />
        )}

        {/* Layer 20: Cinematic Overlays (Blur/Gradient) */}
        <div className={`absolute inset-0 bg-[#120e16]/55 backdrop-blur-[2px] pointer-events-none transition-opacity duration-[2000ms] z-20 ${isTheatreMode ? 'opacity-0' : 'opacity-100'}`}>
          {!isTheatreMode && (
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#120e16]/85 to-[#120e16]"></div>
          )}
        </div>
      </div>
    </div>
  );
};

const AppLayout: React.FC = () => {
  const { isTheatreMode } = useContent();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const bookingType = searchParams.get('booking');
  const contactSubject = searchParams.get('contact');

  // Dynamically update SEO canonical and OG tags on route change
  useEffect(() => {
    // Remove trailing slash if any (except root) to maintain clean canonical URLs
    let canonicalPath = location.pathname;
    if (canonicalPath.endsWith('/') && canonicalPath.length > 1) {
      canonicalPath = canonicalPath.slice(0, -1);
    }

    const canonicalUrl = `https://saltiumgames.com${canonicalPath}`;

    // Manage Canonical Link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', canonicalUrl);
    } else {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      canonicalLink.setAttribute('href', canonicalUrl);
      document.head.appendChild(canonicalLink);
    }

    // Manage Open Graph URL
    let ogUrlTag = document.querySelector('meta[property="og:url"]');
    if (ogUrlTag) {
      ogUrlTag.setAttribute('content', canonicalUrl);
    } else {
      ogUrlTag = document.createElement('meta');
      ogUrlTag.setAttribute('property', 'og:url');
      ogUrlTag.setAttribute('content', canonicalUrl);
      document.head.appendChild(ogUrlTag);
    }
  }, [location.pathname]);

  // Handle auto-scroll and scroll lock when Theatre Mode is triggered
  useEffect(() => {
    if (isTheatreMode) {
      // Force scroll to top (safety catch in case smooth scroll wasn't exact)
      window.scrollTo({ top: 0, behavior: 'instant' });
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isTheatreMode]);

  const onOpenBooking = (type: string = 'full-cycle') => {
    setSearchParams({ booking: type });
  };

  const onOpenContact = (subject: string = 'full-cycle') => {
    setSearchParams({ contact: subject });
  };

  const closeModals = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('booking');
    newParams.delete('contact');
    setSearchParams(newParams);
  };

  const bookingState = useMemo(() => ({
    isOpen: !!bookingType,
    type: bookingType || 'full-cycle'
  }), [bookingType]);

  const contactState = useMemo(() => ({
    isOpen: !!contactSubject,
    subject: contactSubject || 'full-cycle'
  }), [contactSubject]);

  return (
    <div className={`flex flex-col min-h-screen bg-[#120e16] text-white transition-all duration-700 ${isTheatreMode ? 'overflow-hidden' : ''}`}>
      <ScrollRestoration />
      <WebGLBackground />
      <Navbar onOpenBooking={() => onOpenBooking('full-cycle')} />

      <main className={`flex-grow transition-all duration-1000 ease-[cubic-bezier(0.19,1,0.22,1)] ${isTheatreMode ? 'translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'
        }`}>
        <div className="pt-24">
          <Suspense fallback={<PageLoader />}>
            <Outlet context={{ onOpenBooking, onOpenContact } as ContextType} />
          </Suspense>
        </div>
        <Footer />
      </main>

      <BookingModal
        isOpen={bookingState.isOpen}
        onClose={closeModals}
        defaultType={bookingState.type}
      />
      <ContactModal
        isOpen={contactState.isOpen}
        onClose={closeModals}
        defaultSubject={contactState.subject}
      />
      <BackButton />
      <InfoButton />
      <ScrollToTopButton />
      <CookieConsent />
    </div>
  );
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    errorElement: <ErrorPage />,
    children: [
      { index: true, element: <Home /> },
      { path: "about", element: <AboutPage /> },
      { path: "technologies", element: <TechnologiesPage /> },
      { path: "technologies/:id", element: <TechnologyDetail /> },
      { path: "services", element: <ServicesPage /> },
      { path: "services/:id", element: <ServiceDetail /> },
      { path: "projects", element: <ProjectsPage /> },
      { path: "projects/:id", element: <ProjectDetail /> },
      { path: "contact", element: <ContactPage /> },
      { path: "team", element: <TeamPage /> },
      { path: "careers", element: <CareersPage /> },
      { path: "faq", element: <FAQPage /> },
      { path: "sitemap", element: <SitemapPage /> },
      { path: "privacy-policy", element: <PrivacyPolicy /> },
      { path: "terms-of-service", element: <TermsOfService /> },
      { path: "imprint", element: <Imprint /> },
      { path: "cookies", element: <CookiesPage /> },
      { path: "admin", element: <Admin /> },
      { path: "game", element: <GamePage /> },
      { path: "hero", element: <Navigate to="/projects/burden-of-revenge" replace /> },
    ],
  },
]);

const App: React.FC = () => {
  return (
    <ContentProvider>
      <RouterProvider router={router} />
    </ContentProvider>
  );
};

export default App;
